import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  FileText,
  Search,
  LogOut,
  Download,
  Eye,
  Loader2,
  BarChart3,
  Calendar,
  RefreshCw,
  Settings,
  Key,
  Trash2,
  UserCog,
} from "lucide-react";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISA_TYPES = [
  { value: "all", label: "All Visa Types" },
  { value: "30 Days Single Entry", label: "30 Days Single Entry" },
  { value: "60 Days Single Entry", label: "60 Days Single Entry" },
  { value: "30 Days Multiple Entry", label: "30 Days Multiple Entry" },
  { value: "60 Days Multiple Entry", label: "60 Days Multiple Entry" },
  { value: "48 Hours Transit Visa", label: "48 Hours Transit Visa" },
  { value: "96 Hours Transit Visa", label: "96 Hours Transit Visa" },
  { value: "Inside Country Visa", label: "Inside Country Visa" },
  { value: "Visa Renewal", label: "Visa Renewal" },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [visaFilter, setVisaFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Staff password change state (admin only)
  const [staffPasswordDialogOpen, setStaffPasswordDialogOpen] = useState(false);
  const [staffNewPassword, setStaffNewPassword] = useState("");
  const [staffConfirmPassword, setStaffConfirmPassword] = useState("");
  const [changingStaffPassword, setChangingStaffPassword] = useState(false);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
    fetchUserRole();
  }, [token, visaFilter]);

  const fetchUserRole = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/auth/me`, { headers });
      setUserRole(response.data.role);
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, lettersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/guarantee-letters`, {
          headers,
          params: {
            search: searchQuery || undefined,
            visa_type: visaFilter !== "all" ? visaFilter : undefined,
          },
        }),
      ]);

      setStats(statsRes.data);
      setLetters(lettersRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (letterId) => {
    if (!window.confirm("Are you sure you want to delete this record? This action cannot be undone.")) {
      return;
    }
    
    setDeletingId(letterId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/guarantee-letter/${letterId}`, { headers });
      toast.success("Record deleted successfully");
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Delete error:", error);
      if (error.response?.status === 403) {
        toast.error("Only admin can delete records");
      } else {
        toast.error("Failed to delete record");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    await fetchData();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
    toast.success("Logged out successfully");
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers }
      );
      toast.success("Password changed successfully");
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password change error:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangeStaffPassword = async () => {
    if (!staffNewPassword || !staffConfirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (staffNewPassword !== staffConfirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (staffNewPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingStaffPassword(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/auth/change-user-password`,
        {
          username: "whitesky",
          new_password: staffNewPassword,
        },
        { headers }
      );
      toast.success("Staff password changed successfully");
      setStaffPasswordDialogOpen(false);
      setStaffNewPassword("");
      setStaffConfirmPassword("");
    } catch (error) {
      console.error("Staff password change error:", error);
      toast.error(error.response?.data?.detail || "Failed to change staff password");
    } finally {
      setChangingStaffPassword(false);
    }
  };

  const handleDownloadPDF = (letterId, referenceNumber) => {
    // Open PDF in new tab which will trigger download
    const pdfUrl = `${API}/guarantee-letter/${letterId}/pdf`;
    window.open(pdfUrl, "_blank");
    toast.success("PDF opened in new tab");
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-xs text-slate-500">White Sky Travel</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/visa-types")}
              data-testid="manage-visa-types-btn"
            >
              <Settings className="w-4 h-4 mr-1" />
              Visa Types
            </Button>
            
            {/* Change Password Dialog */}
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4 mr-1" />
                  Password
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                  <DialogDescription>
                    Enter your current password and choose a new one.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 chars)"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setPasswordDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    data-testid="submit-password-change-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Change Staff Password Dialog (Admin Only) */}
            {userRole === "admin" && (
              <Dialog open={staffPasswordDialogOpen} onOpenChange={setStaffPasswordDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="change-staff-password-btn"
                  >
                    <UserCog className="w-4 h-4 mr-1" />
                    Staff
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[400px]">
                  <DialogHeader>
                    <DialogTitle>Change Staff Password</DialogTitle>
                    <DialogDescription>
                      Set a new password for the staff account (whitesky).
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="staff-new-password">New Password</Label>
                      <Input
                        id="staff-new-password"
                        type="password"
                        value={staffNewPassword}
                        onChange={(e) => setStaffNewPassword(e.target.value)}
                        placeholder="Enter new password (min 6 chars)"
                        data-testid="staff-new-password-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="staff-confirm-password">Confirm Password</Label>
                      <Input
                        id="staff-confirm-password"
                        type="password"
                        value={staffConfirmPassword}
                        onChange={(e) => setStaffConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        data-testid="staff-confirm-password-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setStaffPasswordDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleChangeStaffPassword}
                      disabled={changingStaffPassword}
                      data-testid="submit-staff-password-btn"
                    >
                      {changingStaffPassword ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Stats Cards */}
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="admin-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="admin-stat-label">Total Letters</p>
                  <p className="admin-stat" data-testid="total-letters-stat">
                    {stats?.total_letters || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </div>

            <div className="admin-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="admin-stat-label">Today</p>
                  <p className="admin-stat" data-testid="today-letters-stat">
                    {stats?.today_letters || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="admin-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="admin-stat-label">Visa Types</p>
                  <p className="admin-stat" data-testid="visa-types-stat">
                    {Object.keys(stats?.visa_type_breakdown || {}).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, passport, or reference..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={visaFilter} onValueChange={setVisaFilter}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="visa-filter-select">
                  <SelectValue placeholder="Filter by visa type" />
                </SelectTrigger>
                <SelectContent>
                  {VISA_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} data-testid="search-btn">
                <Search className="w-4 h-4 mr-1" />
                Search
              </Button>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th>Reference</th>
                    <th>Applicants</th>
                    <th>Count</th>
                    <th>Guarantor</th>
                    <th>Visa Type</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {letters.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-500">
                        No guarantee letters found
                      </td>
                    </tr>
                  ) : (
                    letters.map((letter) => (
                      <tr key={letter.id}>
                        <td className="font-mono text-sm" data-testid={`ref-${letter.id}`}>
                          {letter.reference_number}
                        </td>
                        <td className="font-medium">
                          {letter.passengers?.[0]?.full_name || "N/A"}
                          {letter.passengers?.length > 1 && (
                            <span className="text-xs text-slate-500 ml-1">
                              +{letter.passengers.length - 1} more
                            </span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">
                            {letter.passengers?.length || 1}
                          </span>
                        </td>
                        <td>{letter.guarantor.full_name}</td>
                        <td className="text-sm">{letter.visa_type}</td>
                        <td className="text-sm text-slate-500">
                          {new Date(letter.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <span className="status-badge active">Active</span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/letter/${letter.id}`)}
                              data-testid={`view-${letter.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPDF(letter.id, letter.reference_number)}
                              data-testid={`download-${letter.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {userRole === "admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(letter.id)}
                                disabled={deletingId === letter.id}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-${letter.id}`}
                              >
                                {deletingId === letter.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
